import { FontWeight } from '@pasteltech/theme-provider'
import { memo, useMemo } from 'react'
import { styled } from 'styled-components'
import 'swiper/css'
import 'swiper/css/pagination'
import { Autoplay, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { DeepNullable } from '../../utilities'
import { StrapiImage, StrapiImageData } from './strapi-image'

const DELAY_MS = 4 * 1000

const Root = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  .swiper-pagination-bullets {
    margin-bottom: ${({ theme }) => theme.sizes.spacings.xxxxl}px;
  }
  .swiper-pagination-bullet {
    width: ${({ theme }) => theme.sizes.spacings.sm}px;
    height: ${({ theme }) => theme.sizes.spacings.sm}px;
    background-color: ${({ theme }) => theme.colors.primary.mainContrast};
  }
`
const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  .swiper-wrapper {
    display: flex;
    align-items: center;
  }
`

const StyledStrapiImage = styled(StrapiImage)`
  width: 100%;
  height: 100%;
`
const ChildContainer = styled.div<{ $position: TitlePosition }>`
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 70%;
  margin: 0 auto;
  justify-content: ${({ $position }) =>
    $position === 'bottom' ? 'flex-end' : 'center'};
  padding: 5% 0;
`

const Title = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary.mainContrast};
  font-size: ${({ theme }) => theme.sizes.fontSize.heading1}px;
  font-weight: ${FontWeight.bold};
`
const SubTitle = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary.mainContrast};
  font-size: ${({ theme }) => theme.sizes.fontSize.title}px;
  font-weight: ${FontWeight.regular};
`
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
export type Cover = DeepNullable<{
  title: string
  subTitle: string
  image: StrapiImageData
}>
type TitlePosition = 'bottom' | 'middle'

type SwipeableImageSectionProps = {
  data: Cover[]
  displayTitleFirst?: boolean
  titlePosition?: TitlePosition
  autoPlay?: boolean
}
export const SwipeableImageSection = memo<SwipeableImageSectionProps>(
  ({ data, displayTitleFirst = true, titlePosition = 'middle' }) => {
    const pagination = useMemo(() => {
      return {
        clickable: true,
      }
    }, [])
    return (
      <Root>
        <StyledSwiper
          pagination={pagination}
          modules={[Autoplay, Pagination]}
          autoplay={{
            delay: DELAY_MS,
            disableOnInteraction: false,
          }}
        >
          {data?.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SwiperSlide key={index}>
              <StyledStrapiImage strapiImage={item.image} />
              <ChildContainer $position={titlePosition}>
                {displayTitleFirst ? (
                  <>
                    <Title>{item.title}</Title>
                    <SubTitle>{item.subTitle}</SubTitle>
                  </>
                ) : (
                  <Wrapper>
                    <SubTitle>{item.subTitle}</SubTitle>
                    <Title>{item.title}</Title>
                  </Wrapper>
                )}
              </ChildContainer>
            </SwiperSlide>
          ))}
        </StyledSwiper>
      </Root>
    )
  },
)

SwipeableImageSection.displayName = 'SwipeableImageSection'
